import { gql } from '@graphql/__generated__';

// All enquiries for a listing, used to chart enquiry volume over time split by
// portal. Bucketing happens client-side (see ListingEnquiriesChart).
export const LISTING_ENQUIRIES_OVER_TIME = gql(/* GraphQL */ `
  query ListingEnquiriesOverTime($lotId: uuid!) {
    enquiries(
      where: { lot_id: { _eq: $lotId } }
      order_by: { created_at: asc }
    ) {
      id
      created_at
      portal_id
      portal {
        id
        name
      }
    }
  }
`);

// All buyer leads for a listing, used to chart buyer-lead volume over time
// split by source (matching, manual, MLS, …). Bucketing happens client-side
// (see ListingBuyerLeadsChart).
export const LISTING_BUYER_LEADS_OVER_TIME = gql(/* GraphQL */ `
  query ListingBuyerLeadsOverTime($lotId: uuid!) {
    buyer_leads(
      where: { lot_id: { _eq: $lotId } }
      order_by: { created_at: asc }
    ) {
      id
      created_at
      source
    }
  }
`);

// All offers for a listing, used to chart offer volume over time split by
// status (pending, accepted, refused). Offers are bucketed by the business
// `made_on` date, not by when the CRM row was created.
export const LISTING_OFFERS_OVER_TIME = gql(/* GraphQL */ `
  query ListingOffersOverTime($lotId: uuid!) {
    offers(where: { lot_id: { _eq: $lotId } }, order_by: { made_on: asc }) {
      id
      made_on
      status
    }
  }
`);

// Conversion-funnel data for a listing. The top two steps come from GA4. The
// buyer part is a strict cohort: each buyer lead contributes at most one first
// successful viewing, and offers are matched to that buyer in the client by
// their business `made_on` date.
// `realadvisor_portal_listings` is the lot's row on the RealAdvisor portal (at
// most one per lot). `live` / `milliseconds_online` say whether it was ever
// published there and `updated_at` when it last went offline; the funnel uses
// both to decide whether the portal steps mean anything (see
// portalStepsVisibility.ts).
export const LISTING_FUNNEL = gql(/* GraphQL */ `
  query ListingFunnel(
    $lotId: uuid!
    $from: timestamptz!
    $to: timestamptz!
    $realadvisorPortalId: uuid!
  ) {
    realadvisor_portal_listings: portal_listings(
      where: {
        lot_id: { _eq: $lotId }
        portal_id: { _eq: $realadvisorPortalId }
      }
    ) {
      id
      live
      milliseconds_online
      updated_at
    }
    enquiries: enquiries_aggregate(
      where: { lot_id: { _eq: $lotId }, created_at: { _gte: $from, _lte: $to } }
    ) {
      aggregate {
        count
      }
    }
    lot_buyer_leads: buyer_leads(where: { lot_id: { _eq: $lotId } }) {
      user_id
      successful_viewings: activities_aggregate(
        where: {
          activity_type: { _eq: visit }
          done: { _eq: true }
          success: { _eq: true }
          due_at: { _gte: $from, _lte: $to }
        }
      ) {
        aggregate {
          min {
            due_at
          }
        }
      }
    }
    lot_offers: offers(where: { lot_id: { _eq: $lotId } }) {
      id
      buyer_id
      made_on
    }
  }
`);

// Publication transitions are retained independently of mutable portal rows.
export const LISTING_DOM = gql(/* GraphQL */ `
  query ListingDom($lotId: uuid!) {
    lots_by_pk(id: $lotId) {
      id
      status
    }
    get_lot_publication_history(args: { p_lot_id: $lotId }) {
      history
    }
  }
`);

// Inputs for the rule-based coach callouts and the momentum pulse. All windows
// are fixed (independent of the global date picker) and passed in as ISO
// timestamps so the same query is shared (and deduped) by the pulse + coach.
export const LISTING_INSIGHTS = gql(/* GraphQL */ `
  query ListingInsights(
    $lotId: uuid!
    $staleFloor: timestamptz!
    $staleBefore: timestamptz!
    $recentFrom: timestamptz!
    $priorFrom: timestamptz!
    $monthFrom: timestamptz!
    $now: timestamptz!
  ) {
    unanswered: enquiries_aggregate(
      where: {
        lot_id: { _eq: $lotId }
        response_activity_id: { _is_null: true }
        created_at: { _gte: $staleFloor, _lt: $staleBefore }
      }
    ) {
      aggregate {
        count
      }
    }
    pending_offers: offers_aggregate(
      where: { lot_id: { _eq: $lotId }, status: { _eq: pending } }
    ) {
      aggregate {
        count
      }
    }
    recent_enquiries: enquiries_aggregate(
      where: { lot_id: { _eq: $lotId }, created_at: { _gte: $recentFrom } }
    ) {
      aggregate {
        count
      }
    }
    prior_enquiries: enquiries_aggregate(
      where: {
        lot_id: { _eq: $lotId }
        created_at: { _gte: $priorFrom, _lt: $recentFrom }
      }
    ) {
      aggregate {
        count
      }
    }
    recent_viewings: activities_aggregate(
      where: {
        activity_type: { _eq: visit }
        due_at: { _gte: $recentFrom, _lte: $now }
        _or: [
          { lot_id: { _eq: $lotId } }
          { buyer_lead: { lot_id: { _eq: $lotId } } }
          { enquiry: { lot_id: { _eq: $lotId } } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    prior_viewings: activities_aggregate(
      where: {
        activity_type: { _eq: visit }
        due_at: { _gte: $priorFrom, _lt: $recentFrom }
        _or: [
          { lot_id: { _eq: $lotId } }
          { buyer_lead: { lot_id: { _eq: $lotId } } }
          { enquiry: { lot_id: { _eq: $lotId } } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    month_enquiries: enquiries_aggregate(
      where: { lot_id: { _eq: $lotId }, created_at: { _gte: $monthFrom } }
    ) {
      aggregate {
        count
      }
    }
    month_viewings: activities_aggregate(
      where: {
        activity_type: { _eq: visit }
        due_at: { _gte: $monthFrom, _lte: $now }
        _or: [
          { lot_id: { _eq: $lotId } }
          { buyer_lead: { lot_id: { _eq: $lotId } } }
          { enquiry: { lot_id: { _eq: $lotId } } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`);

// Subject-listing attributes used to find a comparable cohort (Phase 2
// benchmarking). Surfaces/rooms/price/coordinates feed the scrapers
// `*_get_similar_listings_cohorts` function.
export const LISTING_BENCHMARK_SUBJECT = gql(/* GraphQL */ `
  query ListingBenchmarkSubject($lotId: uuid!) {
    lots_by_pk(id: $lotId) {
      id
      sale_price
      lot_reference
      lot_reference_aliases {
        reference_alias_decoded
      }
      property {
        id
        lat
        lng
        number_of_rooms
        living_surface
        built_surface
        usable_surface
        property_type {
          main_type
          name
        }
      }
    }
  }
`);

// Comparable listings from the scrapers schema (run with the `scrapers` Apollo
// client). Same `*_get_similar_listings_cohorts` function the CMA uses, with the
// full card field set (image, address, url, price, dates) so we can render real
// online/offline comparable cards. One country branch resolves per call via the
// `@include` flags.
export const LISTING_COMPARABLES = gql(/* GraphQL */ `
  query ListingComparables(
    $built_surface: Float
    $lat: Float
    $living_surface: Float
    $lng: Float
    $number_of_rooms: Int
    $property_main_type: String
    $sale_price: Float
    $usable_surface: Float
    $limit: Int
    $ch: Boolean!
    $es: Boolean!
    $fr: Boolean!
    $it: Boolean!
  ) {
    ch_get_similar_listings_cohorts(
      args: {
        _built_surface: $built_surface
        _lat: $lat
        _living_surface: $living_surface
        _lng: $lng
        _number_of_rooms: $number_of_rooms
        _property_main_type: $property_main_type
        _sale_price: $sale_price
        _usable_surface: $usable_surface
        _limit: $limit
      }
    ) @include(if: $ch) {
      id
      distance_in_meters
      listing {
        id
        state_id
        original_id
        lot_reference
        agency_reference
        state {
          name
        }
        url
        property_type
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        valid_from
        country_code
        route
        street_number
        postcode
        locality
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    es_get_similar_listings_cohorts(
      args: {
        _built_surface: $built_surface
        _lat: $lat
        _living_surface: $living_surface
        _lng: $lng
        _number_of_rooms: $number_of_rooms
        _property_main_type: $property_main_type
        _sale_price: $sale_price
        _usable_surface: $usable_surface
        _limit: $limit
      }
    ) @include(if: $es) {
      id
      distance_in_meters
      listing {
        id
        state_id
        original_id
        lot_reference
        agency_reference
        state {
          name
        }
        url
        property_type
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        valid_from
        country_code
        route
        street_number
        postcode
        locality
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    fr_get_similar_listings_cohorts(
      args: {
        _built_surface: $built_surface
        _lat: $lat
        _living_surface: $living_surface
        _lng: $lng
        _number_of_rooms: $number_of_rooms
        _property_main_type: $property_main_type
        _sale_price: $sale_price
        _usable_surface: $usable_surface
        _limit: $limit
      }
    ) @include(if: $fr) {
      id
      distance_in_meters
      listing {
        id
        state_id
        original_id
        lot_reference
        agency_reference
        state {
          name
        }
        url
        property_type
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        valid_from
        country_code
        route
        street_number
        postcode
        locality
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    it_get_similar_listings_cohorts(
      args: {
        _built_surface: $built_surface
        _lat: $lat
        _living_surface: $living_surface
        _lng: $lng
        _number_of_rooms: $number_of_rooms
        _property_main_type: $property_main_type
        _sale_price: $sale_price
        _usable_surface: $usable_surface
        _limit: $limit
      }
    ) @include(if: $it) {
      id
      distance_in_meters
      listing {
        id
        state_id
        original_id
        lot_reference
        agency_reference
        state {
          name
        }
        url
        property_type
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        valid_from
        country_code
        route
        street_number
        postcode
        locality
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
  }
`);

export const LISTING_TRANSACTIONS = gql(/* GraphQL */ `
  query ListingTransactions($target_property_id: uuid!) {
    get_property_transactions_distance(
      args: {
        p_target_property_id: $target_property_id
        p_max_distance_meters: 50000
        p_max_results: 10
        p_match_main_type: true
        p_years_back: 2
      }
    ) {
      id
      transaction_id
      distance_in_meters
      transaction {
        id
        purchase_price
        signature_date
        locality
        locality_id
        living_surface
        built_surface
        number_of_rooms
        number_of_bedrooms
        postcode
        property_type_name
      }
    }
  }
`);

export const LISTING_TRANSACTION_PRICE_STATS = gql(/* GraphQL */ `
  query ListingTransactionPriceStats(
    $placeIds: [uuid!]
    $dateMin: date!
    $ch: Boolean!
    $es: Boolean!
    $fr: Boolean!
    $it: Boolean!
  ) {
    ch_price_stats_historical(
      where: {
        place_id: { _in: $placeIds }
        date: { _gte: $dateMin }
        number_of_rooms: { _eq: 0 }
        property_main_type: { _eq: "ALL" }
      }
      order_by: { date: asc }
    ) @include(if: $ch) {
      place_id
      date
      sale_price_m2
    }
    es_price_stats_historical(
      where: {
        place_id: { _in: $placeIds }
        date: { _gte: $dateMin }
        number_of_rooms: { _eq: 0 }
        property_main_type: { _eq: "ALL" }
      }
      order_by: { date: asc }
    ) @include(if: $es) {
      place_id
      date
      sale_price_m2
    }
    fr_price_stats_historical(
      where: {
        place_id: { _in: $placeIds }
        date: { _gte: $dateMin }
        number_of_rooms: { _eq: 0 }
        property_main_type: { _eq: "ALL" }
      }
      order_by: { date: asc }
    ) @include(if: $fr) {
      place_id
      date
      sale_price_m2
    }
    it_price_stats_historical(
      where: {
        place_id: { _in: $placeIds }
        date: { _gte: $dateMin }
        number_of_rooms: { _eq: 0 }
        property_main_type: { _eq: "ALL" }
      }
      order_by: { date: asc }
    ) @include(if: $it) {
      place_id
      date
      sale_price_m2
    }
  }
`);

// Per-listing GA4 impressions / listing views via the `listing_ga4_stats`
// Hasura action (handler in functions/src/hasura-actions/routes/
// get-listing-ga4-stats.ts), which authorizes the lot under the caller's token,
// resolves its public aggregator id, and reads the ClickHouse listing_impressions
// table. Rows are daily counts split by event (`view_item` = listing views,
// `view_item_list` = impressions) and `item_list_id` (placement / source).
export const LISTING_GA4_STATS = gql(/* GraphQL */ `
  query ListingGa4Stats($lotId: uuid!, $dateFrom: String!, $dateTo: String!) {
    listing_ga4_stats(lot_id: $lotId, date_from: $dateFrom, date_to: $dateTo) {
      data {
        day
        event_name
        item_list_id
        count
      }
    }
  }
`);
